import React, { useEffect, useMemo, useState } from 'react';
import { Message, Toggle } from 'rsuite';
import { useAuth } from '../contexts/AuthContext';
import { useUpdateEmployee } from '../hooks/useQueries';
import ChoiceWheel from '../components/shared/ChoiceWheel';
import './TimeOffPage.css';

const TIME_OFF_MEETING_INVITES_PREF_KEY = 'time_off_meeting_invites_enabled';

const TIME_OFF_CHOICES = [
  {
    key: 'create',
    path: '/time-off/create',
    icon: 'bi-calendar2-plus',
    title: 'Create a New Request',
    description: 'Open the full-page request flow to select a date range and submit.',
  },
  {
    key: 'planner',
    path: '/time-off/planner',
    icon: 'bi-airplane',
    title: 'Vacation Planner',
    tag: 'Preview',
    description: 'Your saved vacation plans, or a new one: how much of a trip your reduction covers, how much needs PATO, and the requests to make.',
  },
  {
    key: 'requests',
    path: '/time-off/requests',
    icon: 'bi-list-check',
    title: 'My Time Off',
    description: 'Browse your requests, filter by status, and open one to edit or withdraw.',
  },
  {
    key: 'calendar',
    path: '/time-off/calendar',
    icon: 'bi-calendar3',
    title: 'View Time Off Calendar',
    description: 'Open a large read-only calendar and select a day to view all time off and close date details.',
  },
];

const normalizePreferences = (value) => {
  if (!value) return {};
  if (typeof value === 'object' && !Array.isArray(value)) return value;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
};

const readMeetingInvitePreference = (value) => {
  if (value === null || value === undefined) return true;
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value !== 0;
  const normalized = String(value).trim().toLowerCase();
  if (['false', '0', 'no', 'off', 'disabled'].includes(normalized)) return false;
  if (['true', '1', 'yes', 'on', 'enabled'].includes(normalized)) return true;
  return true;
};

export default function TimeOffPage() {
  const auth = useAuth();
  const updateEmployeeMutation = useUpdateEmployee();
  const [meetingInvitePreferenceError, setMeetingInvitePreferenceError] = useState(null);

  const currentPreferences = useMemo(
    () => normalizePreferences(auth.employee?.preferences),
    [auth.employee?.preferences]
  );
  const currentMeetingInviteSetting = useMemo(
    () => readMeetingInvitePreference(currentPreferences?.[TIME_OFF_MEETING_INVITES_PREF_KEY]),
    [currentPreferences]
  );
  const [meetingInvitesEnabled, setMeetingInvitesEnabled] = useState(currentMeetingInviteSetting);

  useEffect(() => {
    setMeetingInvitesEnabled(currentMeetingInviteSetting);
  }, [currentMeetingInviteSetting]);

  const saveMeetingInvitePreference = async (nextValue) => {
    if (!auth.employee?.id) return;

    const nextSetting = Boolean(nextValue);
    const previousSetting = meetingInvitesEnabled;

    setMeetingInvitePreferenceError(null);
    setMeetingInvitesEnabled(nextSetting);

    try {
      await updateEmployeeMutation.mutateAsync({
        id: auth.employee.id,
        preferences: {
          ...currentPreferences,
          [TIME_OFF_MEETING_INVITES_PREF_KEY]: nextSetting,
        }
      });
    } catch (error) {
      setMeetingInvitesEnabled(previousSetting);
      setMeetingInvitePreferenceError(String(error?.message || error));
    }
  };

  return (
    <div className="d-flex flex-column gap-3">
      <div className="d-flex justify-content-end align-items-start flex-wrap gap-2">
        <div className="d-flex flex-column align-items-end gap-1">
          <div className="d-flex align-items-center gap-2 border rounded px-2 py-1" style={{ minHeight: 38 }}>
            <span className="text-muted small">Receive meeting invites for time off</span>
            <Toggle
              checked={meetingInvitesEnabled}
              checkedChildren="On"
              unCheckedChildren="Off"
              disabled={updateEmployeeMutation.isPending}
              onChange={(next) => saveMeetingInvitePreference(Boolean(next))}
            />
          </div>
          <div className="text-muted" style={{ fontSize: 12 }}>
            Controls whether you are included on company time-off Teams invites.
          </div>
        </div>
      </div>

      {meetingInvitePreferenceError ? (
        <Message type="error" showIcon>
          {meetingInvitePreferenceError}
        </Message>
      ) : null}

      <ChoiceWheel items={TIME_OFF_CHOICES} title="Time Off" reservedHeight={150} />
    </div>
  );
}
